<template>
  <b-sidebar
    id="sidebar"
    :visible="isSidebarActive"
    bg-variant="white"
    sidebar-class="sidebar-lg"
    shadow
    backdrop
    no-header
    no-close-on-backdrop
    right
    @shown="loadData"
    @hidden="resetForm"
    @change="(val) => $emit('update:is-sidebar-active', val)"
  >
    <template #default="{ hide }">
      <!-- Header -->
      <div
        class="
          d-flex
          justify-content-between
          align-items-center
          content-sidebar-header
          px-2
          py-1
        "
      >
        <h5 class="mb-0 text-capitalize">
          {{ isAdd ? $t("actions.add") + " Subtipo" : "Editar Subtipo" }}
        </h5>

        <feather-icon
          class="ml-1 cursor-pointer"
          icon="XIcon"
          size="16"
          @click="hide"
        />
      </div>

      <!-- BODY -->
      <validation-observer #default="{ handleSubmit }" ref="refFormObserver">
        <b-form
          class="p-2"
          @submit.prevent="handleSubmit(onSubmit)"
          @reset.prevent="resetForm"
        >
          <!-- ── Padres: Origen (nivel 1, solo Siniestro) y Tipo (nivel 2) ── -->
          <template v-if="isAdd">
            <b-form-group v-if="withOrigin" label="Origen" label-for="tipo">
              <v-select
                id="tipo"
                v-model="selectedTipoId"
                :options="tipoOptions"
                :reduce="(o) => o.value"
                label="label"
                :clearable="false"
                placeholder="Selecciona un Origen"
                @input="onTipoChange"
              />
            </b-form-group>

            <b-form-group label="Tipo" label-for="subtipo">
              <v-select
                id="subtipo"
                v-model="selectedSubtipoId"
                :options="subtipoOptions"
                :reduce="(o) => o.value"
                label="label"
                :disabled="!selectedTipoId"
                placeholder="Selecciona un Tipo"
              />
            </b-form-group>
          </template>

          <!-- En edición mostramos el padre como referencia (solo lectura) -->
          <template v-else>
            <b-form-group v-if="withOrigin" label="Origen">
              <b-form-input :value="parentInfo.tipo" disabled />
            </b-form-group>
            <b-form-group label="Tipo">
              <b-form-input :value="parentInfo.subtipo" disabled />
            </b-form-group>
          </template>

          <hr />

          <!-- ── Nivel 3 (lo que se captura) ── -->
          <label class="font-weight-bold">Subtipo</label>

          <validation-provider
            #default="validationContext"
            name="name"
            rules="required"
          >
            <b-form-group label="Nombre" label-for="name">
              <b-form-input
                id="name"
                v-model="itemData.name"
                :state="getValidationState(validationContext)"
                trim
                :placeholder="withOrigin ? 'Ej. Culposo' : 'Ej. Sin imagen'"
              />
              <b-form-invalid-feedback>
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <validation-provider
            #default="validationContext"
            name="code"
            rules="required"
          >
            <b-form-group label="Código" label-for="code">
              <b-form-input
                id="code"
                v-model="itemData.code"
                :state="getValidationState(validationContext)"
                trim
                :placeholder="withOrigin ? 'Ej. CULPOSO' : 'Ej. SIN_IMAGEN'"
              />
              <b-form-invalid-feedback>
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- Form Actions -->
          <div class="d-flex mt-2">
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              variant="primary"
              class="mr-2 text-capitalize"
              type="submit"
            >
              {{ $t("actions.save") }}
            </b-button>
            <b-button
              v-ripple.400="'rgba(186, 191, 199, 0.15)'"
              type="button"
              variant="outline-secondary"
              class="text-capitalize"
              @click="hide"
            >
              {{ $t("actions.cancel") }}
            </b-button>
          </div>
        </b-form>
      </validation-observer>
    </template>
  </b-sidebar>
</template>

<script>
import {
  BSidebar,
  BForm,
  BFormGroup,
  BFormInput,
  BFormInvalidFeedback,
  BButton,
} from "bootstrap-vue";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import vSelect from "vue-select";
import { ref, computed } from "@vue/composition-api";
import { required } from "@validations";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import formValidation from "@core/comp-functions/forms/form-validation";
import Ripple from "vue-ripple-directive";
import store from "@/store";
import { showOrigin, isDefaultOrigin } from "@/views/helpdesk/catalogConfig";
import { useUtils as useI18nUtils } from "@core/libs/i18n";


export default {
  components: {
    BSidebar,
    BForm,
    BFormGroup,
    BFormInput,
    BFormInvalidFeedback,
    BButton,
    vSelect,
    ValidationProvider,
    ValidationObserver,
  },
  directives: {
    Ripple,
  },
  model: {
    prop: "isSidebarActive",
    event: "update:is-sidebar-active",
  },
  props: {
    isSidebarActive: {
      type: Boolean,
      required: true,
    },
    itemEdit: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    isAdd: {
      type: Boolean,
      required: true,
    },
    catalog: {
      type: String,
      default: "siniestro",
    },
  },
  data() {
    return {
      required,
    };
  },
  setup(props, { emit }) {
    const { t } = useI18nUtils();
    const trans = {
      "messages.edit_save.successfully_created": t(
        "messages.edit_save.successfully_created"
      ),
      "messages.edit_save.updated_successfully": t(
        "messages.edit_save.updated_successfully"
      ),
    };

    const blankData = { code: "", name: "" }; // el nivel 3 a capturar
    // Siniestro elige Origen; Incidente usa el origen fijo (se preselecciona y no se muestra)
    const withOrigin = showOrigin(props.catalog);

    const toast = useToast();

    const itemData = ref(JSON.parse(JSON.stringify(blankData)));

    // Selección de padres (alta)
    const roots = ref([]); // raíces con children_recursive (del getAll)
    const selectedTipoId = ref(null);
    const selectedSubtipoId = ref(null);

    // Edición
    const rootTree = ref(null); // raíz completa del nodo editado
    const editingId = ref(null); // id del nodo nivel 3 editado
    const parentInfo = ref({ tipo: "", subtipo: "" });

    const resetData = () => {
      itemData.value = JSON.parse(JSON.stringify(blankData));
      selectedTipoId.value = null;
      selectedSubtipoId.value = null;
      rootTree.value = null;
      editingId.value = null;
      parentInfo.value = { tipo: "", subtipo: "" };
    };

    // Opciones de los selects
    const tipoOptions = computed(() =>
      roots.value.map((r) => ({
        value: r.id,
        label: `${r.name} (${r.code})`,
      }))
    );

    const subtipoOptions = computed(() => {
      const root = roots.value.find((r) => r.id === selectedTipoId.value);
      const lvl2 = (root && root.children_recursive) || [];
      return lvl2.map((s) => ({
        value: s.id,
        label: `${s.name} (${s.code})`,
      }));
    });

    const onTipoChange = () => {
      // al cambiar de Tipo, reseteamos el Subtipo elegido
      selectedSubtipoId.value = null;
    };

    // ── Helpers de árbol (PUT) ──
    const toPutNode = (node, isRoot) => {
      const out = {};
      if (node.id != null) out.id = node.id;
      if (isRoot) out.catalog = node.catalog || props.catalog;
      out.code = node.code;
      out.name = node.name;
      const kids = node.children_recursive || node.children || [];
      if (isRoot || kids.length) {
        out.children = kids.map((k) => toPutNode(k, false));
      }
      return out;
    };

    const findNode = (nodes, targetId) => {
      for (const n of nodes || []) {
        if (n.id === targetId) return n;
        const found = findNode(
          n.children_recursive || n.children || [],
          targetId
        );
        if (found) return found;
      }
      return null;
    };

    const onSubmit = () => {
      let dispatch;

      if (props.isAdd) {
        if (!selectedTipoId.value || !selectedSubtipoId.value) {
          toast({
            component: ToastificationContent,
            props: {
              title: withOrigin ? "Selecciona el Origen y el Tipo." : "Selecciona el Tipo.",
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
          return;
        }

        // Clonamos la raíz del Tipo elegido y colgamos el nuevo nivel 3
        // del Subtipo (nivel 2) elegido, sin tocar lo demás.
        const root = JSON.parse(
          JSON.stringify(
            roots.value.find((r) => r.id === selectedTipoId.value)
          )
        );
        const lvl2 = findNode(root.children_recursive, selectedSubtipoId.value);
        if (!lvl2) {
          toast({
            component: ToastificationContent,
            props: {
              title: "No se encontró el Tipo seleccionado.",
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
          return;
        }
        lvl2.children_recursive = lvl2.children_recursive || [];
        lvl2.children_recursive.push({
          code: itemData.value.code,
          name: itemData.value.name,
          children: [],
        });

        dispatch = store.dispatch("helpdesk/editFailureCategories", {
          id: root.id, // PUT al id de la raíz
          params: toPutNode(root, true),
        });
      } else {
        // Edición: solo cambia el nodo nivel 3 por su id.
        if (!rootTree.value || !editingId.value) {
          toast({
            component: ToastificationContent,
            props: {
              title: "No se pudo identificar el registro a editar.",
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
          return;
        }
        const root = JSON.parse(JSON.stringify(rootTree.value));
        const node = findNode(root.children_recursive, editingId.value);
        if (node) {
          node.code = itemData.value.code;
          node.name = itemData.value.name;
        }
        dispatch = store.dispatch("helpdesk/editFailureCategories", {
          id: root.id,
          params: toPutNode(root, true),
        });
      }

      dispatch
        .then((response) => {
          if (response && response.success) {
            toast({
              component: ToastificationContent,
              props: {
                title: props.isAdd
                  ? trans["messages.edit_save.successfully_created"]
                  : trans["messages.edit_save.updated_successfully"],
                icon: "CheckIcon",
                variant: "success",
              },
            });
            emit("refetch-data");
            emit("update:is-sidebar-active", false);
          } else {
            toast({
              component: ToastificationContent,
              props: {
                title: (response && response.message) || "No se pudo guardar.",
                icon: "AlertTriangleIcon",
                variant: "danger",
              },
            });
          }
        })
        .catch((error) => {
          toast({
            component: ToastificationContent,
            props: {
              title: error?.response?.data?.message || "Error al guardar.",
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        });
    };

    const { refFormObserver, getValidationState, resetForm } =
      formValidation(resetData);

    const loadData = async () => {
      if (props.isAdd) {
        // Cargamos las raíces (con sus subtipos) para poblar los selects.
        try {
          await store.dispatch("helpdesk/getAllFailureCategories", { catalog: [props.catalog] });
        } catch (e) {
          // seguimos con lo que haya
        }
        roots.value =
          (store.state.helpdesk && store.state.helpdesk.items) || [];
        const defaultRoot = roots.value.find(isDefaultOrigin) || roots.value[0];
        selectedTipoId.value = defaultRoot ? defaultRoot.id : null;
      } else if (props.itemEdit) {
        const edit = JSON.parse(JSON.stringify(props.itemEdit));
        rootTree.value = edit.__root || null;
        editingId.value = edit.id;
        itemData.value = {
          code: edit.code || "",
          name: edit.name || "",
        };
        parentInfo.value = {
          tipo: edit.__root
            ? `${edit.__root.name} (${edit.__root.code})`
            : "",
          subtipo: edit.__parent
            ? `${edit.__parent.name} (${edit.__parent.code})`
            : "",
        };
      }
    };

    return {
      withOrigin,
      itemData,
      roots,
      selectedTipoId,
      selectedSubtipoId,
      tipoOptions,
      subtipoOptions,
      onTipoChange,
      parentInfo,
      onSubmit,
      refFormObserver,
      getValidationState,
      loadData,
      resetForm,
    };
  },
};
</script>